import {
  ForgotPasswordRequest,
  useForgotPassword,
} from "@/services/mutations/auth";
import { Hand } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormMessage,
  FormItem,
  FormControl,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Envelope, Fingerprint } from "@phosphor-icons/react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Mixpanel } from "@/lib/mixpanel";
import { APP_TYPE } from "@/lib/constants";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    mutate: forgotPassword,
    isPending,
    isSuccess,
  } = useForgotPassword({
    onSuccess: () => {
      Mixpanel.track("Forgot Password Submitted Successfully");
    },
    onError: () => {
      Mixpanel.track("Forgot Password Submitted Failed");
    },
  });
  const forgotPasswordSchema = z.object({
    email: z
      .string()
      .email({ message: t("error_message.invalid_email") })
      .min(2, {
        message: t("error_message.minimum_characters", { count: 2 }),
      }),
  });

  const form = useForm<ForgotPasswordRequest>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = (data: ForgotPasswordRequest) => {
    forgotPassword({ ...data, app: APP_TYPE.MOBILE });
  };

  return (
    <>
      <title>{t("title.forget_password")}</title>

      <div className="flex h-screen items-center justify-center bg-[url('/images/wave-bg.png')] bg-cover bg-center bg-no-repeat">
        {!isSuccess ? (
          <div className="flex h-full w-full max-w-md flex-col items-center justify-between px-5 py-10 text-3xl font-bold">
            <span className="h-[38px]" />
            <div className="flex w-full flex-col items-center">
              <Hand size={56} />
              <span className="mb-8 mt-2 text-[26px] leading-8 text-white">
                {t("title.forget_password")}
              </span>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="w-full space-y-3"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            id="email"
                            placeholder={t("label.email")}
                            {...field}
                            icon={Envelope}
                          />
                        </FormControl>
                        <FormMessage data-testid="email-error" />
                      </FormItem>
                    )}
                  />

                  <Button
                    isPending={isPending}
                    disabled={isPending}
                    className="w-full"
                    type="submit"
                  >
                    {t("button.reset_password")}
                  </Button>
                </form>
              </Form>
            </div>

            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="mt-4 w-full"
            >
              {t("button.login")}
            </Button>
          </div>
        ) : (
          <EmailSentConfirmation email={form.getValues("email")} />
        )}
      </div>
    </>
  );
};

const EmailSentConfirmation = ({ email }: { email: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex w-full max-w-sm flex-col items-center justify-center">
      <div className="relative w-fit">
        <Fingerprint color="#719BAD" size={40} />
        <span className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-full bg-[#0DB873]">
          <Check color="#fff" size={10} weight="bold" />
        </span>
      </div>

      <span className="mb-6 mt-2 max-w-[224px] text-center text-[26px] leading-8 text-white">
        {t("success_message.forgot_password_title")}
      </span>

      <span className="mb-6 mt-2 w-full max-w-[217px] text-center leading-6 tracking-wide text-[#98B2D3]">
        {t("success_message.forgot_password_description", { email })}
      </span>

      <Button onClick={() => navigate("/login")} className="w-full">
        {t("button.login")}
      </Button>
    </div>
  );
};

export default ForgotPassword;
